import {
  PageType,
  type ReportPageConfig,
} from '../components/PageConfigProvider/types';
import { FilterId } from '../filters/types/filters';
import { buildReportPages } from './report-page-builder';

const companyParam = { parameterId: 'company-parameter', grouped: false };
const competitorsGrouped = {
  parameterId: 'competitors-parameter',
  grouped: true,
};
const competitorsUngrouped = {
  parameterId: 'competitors-parameter',
  grouped: false,
};
const geographyParam = { parameterId: 'geography-parameter', grouped: false };
const roleParam = { parameterId: 'role-parameter', grouped: false };

const industryOverviewPlotParameters = [
  competitorsUngrouped,
  geographyParam,
  roleParam,
];

const salaryTablePlotParameters = [
  companyParam,
  competitorsGrouped,
  geographyParam,
  roleParam,
];

const competitorBoxPlotParameters = [
  companyParam,
  competitorsUngrouped,
  geographyParam,
  roleParam,
];

export const reportCompanyCompensationBenchmark: ReportPageConfig = (() => {
  const { items, grid } = buildReportPages('company-compensation-benchmark', [
    [
      {
        id: 'about-text',
        title: '',
        position: { height: 32 },
        plot: {
          chartType: 'ai-summary',
          textHtml: '## About\nDescription',
        },
      },
    ],
    [
      {
        id: 'comp-bench-industry-overview-text',
        title: '',
        position: { width: 26, height: 21 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Industry overview\n\nBenchmarking a company\'s compensation against its peers requires careful consideration of comparable organizations. We select these peer companies using our competitors model, which accounts for factors such as industry and company size.\n\nHere, we show an overview of the peer group selected. We select the 50 closest companies to {{PARAM "Company"}} from our competitors model and weight them relative to their current employee headcounts.\n\nWe observe around {{FORMAT (SUM "headcount") format="SI"}} {{filter "ROLE"}} positions in {{filter "GEOGRAPHY"}} currently employed in one of these peer companies. In the peer group, {{ARG_MAX "headcount"}} has the largest number of employees, with {{FORMAT (SHARE "headcount" entity=(ARG_MAX "headcount")) format="PERCENTAGE"}} of the total headcount.',
          plotParameters: industryOverviewPlotParameters,
        },
      },
      {
        id: 'comp-bench-industry-treemap',
        title: 'Composition of {{PARAM "Company"}}\'s peer group',
        position: { x: 26, y: 2, width: 22, height: 19 },
        plot: {
          chartType: 'treemap',
          groupBySegment: false,
          defaultMetricOptions: ['headcount'],
          plotParameters: [competitorsUngrouped],
        },
      },
    ],
    [
      {
        id: 'comp-bench-salary-intro',
        title: '',
        position: { height: 7 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Salary distribution compared to peer group\n\nUnderstanding the salary distribution of a company relative to its peer group is essential in compensation benchmarking. Here, we show how the salary and total compensation at {{PARAM "Company"}} compares to its peers.',
        },
      },
      {
        id: 'comp-bench-salary-table',
        title: 'Salary of current employees in role and geography selected',
        position: { y: 7, height: 9 },
        plot: {
          chartType: 'table',
          defaultMetricOptions: ['base_salary_percentiles'],
          enableRowSelection: false,
          plotParameters: salaryTablePlotParameters,
        },
        isTitleManuallySet: true,
      },
      {
        id: 'e1a002c8-5bcf-409d-a6ed-a7381d604b9c',
        title:
          'Total compensation of current employees in role and geography selected',
        position: { y: 16, height: 9 },
        plot: {
          chartType: 'table',
          enableRowSelection: false,
          plotParameters: salaryTablePlotParameters,
          defaultMetricOptions: ['total_compensation_percentiles'],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'comp-bench-box-intro',
        title: '',
        position: { height: 10 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Salary distribution compared to peer group\n\nThere can be pros and cons of having higher variation in salaries even within the same role or geographical area in a company. For example, a wide range of salaries in a company may be the result of employers offering competitive compensation packages to attract and retain top talent. However, a range that is too wide may create perceptions of inequality within the organization, leading to conflict or decreased morale if employees believe that others are unfairly compensated for similar levels of work or contribution.\n\nHere, we see how {{PARAM "Company"}} compares to its peer group in terms of salary distribution.',
        },
      },
      {
        id: 'comp-bench-box-plot',
        title:
          'Salary distribution of {{filter "ROLE"}} in {{filter "GEOGRAPHY"}}',
        info: {
          text: '10th, 25th, 50th, 75th, and 90th percentiles of current employee salaries in role and geography selected',
        },
        position: { x: 8, y: 10, width: 32, height: 9 },
        plot: {
          chartType: 'box',
          defaultMetricOptions: ['base_salary'],
          showQuartiles: true,
          plotParameters: salaryTablePlotParameters,
          plotFilters: [],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'comp-bench-competitor-intro',
        title: '',
        position: { height: 14 },
        plot: {
          chartType: 'text',
          textHtml: `## Salary distribution compared to competitors\n\nCompensation benchmarking allows employers to assess the competitiveness of their compensation package relative to those of its peers.\n\nWhile comparing salaries between employees at a company and its peer group as a whole provides a high-level view of where a company's compensation package stands, Revelio Labs' rich workforce data allows for a more detailed company-level comparison as well. This enables organizations to determine which specific competitors they could recruit talent from, which they might be in danger of losing talent to, and how they can set compensation to match or exceed certain companies.\n\nHere, we compare {{PARAM "Company"}}'s salaries to those of its fifteen closest competitors. Companies paying lower salaries than their peers may benefit from strategically considering their compensation packages if they seek to increase employee retention and satisfaction.`,
        },
      },
      {
        id: 'comp-bench-competitor-box',
        title:
          'Salary distribution of {{filter "ROLE"}} in {{filter "GEOGRAPHY"}}',
        info: {
          text: '10th, 25th, 50th, 75th, and 90th percentiles of current employee salaries in role and geography selected',
        },
        position: { x: 1, y: 14, width: 47, height: 18 },
        plot: {
          chartType: 'box',
          defaultMetricOptions: ['base_salary'],
          showQuartiles: true,
          plotParameters: competitorBoxPlotParameters,
          plotFilters: [],
        },
        isTitleManuallySet: true,
      },
    ],
  ]);

  return {
    id: 'report-company-compensation-benchmark',
    name: 'Compensation Benchmark',
    type: PageType.REPORT,
    owner: {
      id: 'db176d03-a841-44ee-bedb-78b2aa87c32a',
      username: 'jeremie@reveliolabs.com',
    },
    sampleFilterGroups:
      'NrBEGEHkFkAUEEByBNUAaEEYJeg3qAJYAmoAXFnEqmqAHYCGAtgKbkQD2TADg3QJ4ACAGSCAknWIBXAM4AXAE790oGSwA2LAMZzCHOgBlC8scRnkwCwlpkB9ANYBGAAwqrNhwBZnr2lq68AqAAurRqmjp6dOZkwAQk7AActgBszraOAMwArACcKbkATCqMrOzwTAwAXvoAdP5MaOJ0WrUqmgBuGqbsDYHKtMbgUgoKLHRy5IpSLAC+oaBy-NxsFIosbIMyAGrGhABGmlMKM1uwVpVKx6eg3BcMSgDKGtq6+jFxRKQUyWkZOfkiiVmKtQBVqnUGk0JK12iwuuoehQ+nwBkQZMNRuNJmRpiwtvBIl1rnNgvMMKAACLwAAq8Fs8EpACkAKqPGnQACiiBpj3wX3Y1LpDOZbI53N5KiWK3Y8gYui0tnUxkmYRekXeFni31AwLKFHgxAAVrI5CxSOT0Sy1ApHlJ9kxCHIzd88VtdjIDkcyAAzBjqNTzYKhMB9Fi6OQcBQyAC0vAUILNCnQmCgVFwaG17DTOBo9BB2YCqJEzWk8iuaoib0MKtMH1A7jsTl8DesTe8LZRQQW4VeUQ+WZ+qXSiUyhWyjkSetBNIAFixBDSE8QF5SWDJ7NCWm1aJ1ujqu2ihiMxhMSbNMwKh9kAOy2QqFRyeRI3zI36fsAAS9se3A4ci3WFd3hfdeiLIItkxU8cTxC9B1AX50iyG8W1KUFYEICYWDGeRAJ3UA90RA9wKPDET2xc9LwSIc-iKfJMkyD8KAAcQUDgpD-Og8LhBEkVAQ8VGPLEz1xE45ionVkjyWxElvcdsiYiB5wAd34bjgN44ieFRQSyOEmCxLgq9QHSdIHyfXJEk8BTaDQwsOL4ABzQRwA4WpBAMORiHwwi+IEyDyJE2CJKSYcMkcJ8ClyRTHlnDhuEIH01OaICCJAoiwO0iD0SgijRJmIzqJM2x0hvG8XHfWyCwocAGBkWdBHgbhuGaLp5Ew5yDAMcAeNA5ESN03KgsMkLrzvRwUhvbJPBSRSACUGHsKQzToQRWPY7h1LSzTMv6QbAoMgqFmlUF1k2dEPS9VY3XRc5CEuZR8vxW57iedVqw+MlgxDUBHJYDhHITbhZ34OMHkTbCUzAZjOUgZi5vgWBPxoeCYbhhGkbzOyWP+wGGGBtFew1GsTDMCwGxYRyohUfwpAmCtQCYGQGBpp1lB7d7+y1YzHAyRSWToJ1zUER45Hlddeoy-q6cUUihsO0laBO9gzt0y7DmusSzlex6-QDZ6hOgkktkQf9yD1tQtmtbC7QdJ0XXPb6KTYzQwYTVgkyh0A5sgAxOX5Iqfb9+aOCOSs+30IxSfrF2WAcSqG1DuOnGyFtY4cRxU7TpOM6z1wOarLnYngiL-lQ6rQEeDgfTkZSHgXTk6CpugNmTDS+sTzRc5bQ28tg47llOsZzuMdXvRu4w7oe83-UtnKDuN9FTZxC2DZka3bXtR1nXNR2KSDzlbDpAANSBEBgFHjIPo-4FP8-oCxiu5qTxcGAAD30Lg0Q6f0ZnIAgVx+ikOoBWtAujRmphQTItRHC1EYheRYg92A-3UH-d0ewNYz31gSIkmsjrfSAA',
    tabs: [
      {
        label: 'In-depth analysis of role compensation vs industry peers',
        id: 'default',
        isActive: true,
        tabContent: {
          id: 'company-compensation-benchmark',
          title: 'Compensation Benchmarking',
          description:
            "Benchmarking a company's salary and compensation structure to its peers provides insights into its ability to acquire and retain talent, its competitiveness in the labor market, and its pay equity.",
          grid,
          items,
          primaryFilter: {
            id: FilterId.COMPANY,
            selectionLimit: 1,
            selectionDefault: 1,
          },
          secondaryFilters: [],
          reportParameters: [
            {
              parameterId: 'company-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: true,
              name: 'Company',
            },
            {
              parameterId: 'competitors-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: false,
              name: 'Company Competitors',
              selectionLimit: 50,
            },
            {
              parameterId: 'geography-parameter',
              filterId: FilterId.GEOGRAPHY,
              isPrimary: false,
              name: 'Geography',
            },
            {
              parameterId: 'role-parameter',
              filterId: FilterId.ROLE,
              isPrimary: false,
              name: 'Role',
            },
          ],
        },
      },
    ],
  };
})();
